/**
 * Utility functions for safe image handling
 * Prevents external dependencies and rate limiting issues
 */

/**
 * Get the full image URL from a relative or absolute path
 * @param {string} imagePath - The image path from database
 * @returns {string} Full URL or empty string
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath;
  // Use relative URL for same-domain deployment
  return imagePath;
};

/**
 * Create a data URL for a local SVG placeholder
 * @param {number} width - Width of placeholder
 * @param {number} height - Height of placeholder
 * @param {string} text - Text to display
 * @returns {string} Data URL for the SVG
 */
export const createPlaceholderDataUrl = (width = 400, height = 300, text = 'No Image') => {
  const svg = `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <rect width="${width}" height="${height}" fill="#e5e7eb" />
      <g transform="translate(${width / 2}, ${height / 2 - 30})">
        <rect x="-40" y="-40" width="80" height="80" fill="none" stroke="#9ca3af" stroke-width="2" />
        <circle cx="-20" cy="-20" r="8" fill="#9ca3af" />
        <path d="M -40 20 L -15 -5 L 10 15 L 40 -20" fill="none" stroke="#9ca3af" stroke-width="2" />
      </g>
      <text x="${width / 2}" y="${height / 2 + 40}" text-anchor="middle" font-size="16" fill="#6b7280" font-family="system-ui, -apple-system, sans-serif">${text}</text>
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/**
 * Handle image error with local fallback
 * @param {Event} event - The error event
 * @param {number} width - Width for placeholder
 * @param {number} height - Height for placeholder
 * @param {string} text - Text for placeholder
 */
export const handleImageError = (event, width = 400, height = 300, text = 'No Image') => {
  if (event && event.target) {
    event.target.src = createPlaceholderDataUrl(width, height, text);
  }
};

/**
 * Preload an image to check if it exists
 * @param {string} url - Image URL to preload
 * @returns {Promise<boolean>} True if image loads successfully
 */
export const preloadImage = (url) => {
  return new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};
